/**
 * useVideoProgress Hook
 * 监听 Cloudflare Stream 视频播放进度并自动保存
 * 使用 Cloudflare Stream SDK 的事件回调
 *
 * ✅ 使用 TanStack Query 实现数据获取和更新
 */

import {
  createComponentLifetime,
  createLatestWriteCoordinator,
  type LatestWriteCoordinator,
} from "@crm/kernel/latest-write";
import {
  unsafeCurrentUtcDate,
  unsafeDateTimeInputToEpochMillis,
} from "@crm/shared/time/beijing-time";
import { Data } from "effect";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { SaveLessonProgressInput } from "../../domain/review-course-inputs";
import { useClearLessonProgress, useSaveLessonProgress } from "./";

type VideoProgressData = {
  currentTime: number; // 当前播放位置(秒)
  duration: number; // 视频总时长(秒)
  percentComplete: number; // 完成百分比
};

type UseVideoProgressOptions = {
  lessonId: string | undefined;
  initialCompleted?: boolean; // 初始完成状态
  completionThreshold?: number; // 完成阈值,默认80%
  saveInterval?: number; // 保存间隔(毫秒),默认5秒
};

type LessonProgressCommand =
  | { readonly kind: "save"; readonly observation: SaveLessonProgressInput }
  | { readonly kind: "clear" };

export class LessonProgressPersistenceError extends Data.TaggedError(
  "LessonProgressPersistenceError",
)<{
  readonly operation: "save" | "clear";
  readonly cause: unknown;
}> {}

type ClearBarrier = {
  readonly promise: Promise<void>;
  readonly reject: (error: LessonProgressPersistenceError) => void;
  readonly resolve: () => void;
};

export type LessonProgressWriter = {
  readonly observe: (observation: SaveLessonProgressInput) => void;
  readonly flush: () => void;
  readonly clear: () => Promise<void>;
  readonly state: () => ReturnType<LatestWriteCoordinator<LessonProgressCommand>["state"]>;
};

function bottomProgressObservation(lessonId: string): SaveLessonProgressInput {
  return {
    lessonId,
    watchedSeconds: 0,
    lastPosition: 0,
    progressPercentage: 0,
    completed: false,
  };
}

export function joinLessonProgressObservation(
  current: SaveLessonProgressInput,
  observed: SaveLessonProgressInput,
): SaveLessonProgressInput {
  return {
    lessonId: current.lessonId,
    watchedSeconds: Math.max(current.watchedSeconds, observed.watchedSeconds),
    lastPosition: Math.max(current.lastPosition, observed.lastPosition),
    progressPercentage: Math.max(current.progressPercentage, observed.progressPercentage),
    completed: current.completed || observed.completed,
  };
}

function persist<A>(operation: "save" | "clear", run: () => PromiseLike<A>): Promise<A> {
  return new Promise<A>((resolve) => {
    resolve(run());
  }).catch((cause: unknown) =>
    Promise.reject(new LessonProgressPersistenceError({ operation, cause })),
  );
}

export function createLessonProgressWriter(options: {
  readonly lessonId: string;
  readonly save: (observation: SaveLessonProgressInput) => PromiseLike<unknown>;
  readonly clear: (lessonId: string) => PromiseLike<unknown>;
}): LessonProgressWriter {
  let aggregate = bottomProgressObservation(options.lessonId);
  let hasObservation = false;
  let acceptsObservations = true;
  let requestSequence = 0;
  let semanticRevision = 0;
  let clearBarrier: ClearBarrier | null = null;
  let coordinator: LatestWriteCoordinator<LessonProgressCommand>;

  const resetAfterClear = () => {
    aggregate = bottomProgressObservation(options.lessonId);
    hasObservation = false;
    acceptsObservations = true;
    semanticRevision = 0;
    coordinator = makeCoordinator();
  };

  const settleClearSuccess = () => {
    const barrier = clearBarrier;
    clearBarrier = null;
    resetAfterClear();
    barrier?.resolve();
  };

  const settleClearFailure = (error: LessonProgressPersistenceError) => {
    const barrier = clearBarrier;
    clearBarrier = null;
    barrier?.reject(error);
  };

  function makeCoordinator(): LatestWriteCoordinator<LessonProgressCommand> {
    return createLatestWriteCoordinator<LessonProgressCommand>({
      entityId: options.lessonId,
      onStateChange: (state) => {
        if (
          state.status === "blocked" &&
          clearBarrier &&
          (state.active.value.kind === "clear" || state.pending?.value.kind === "clear")
        ) {
          settleClearFailure(
            new LessonProgressPersistenceError({
              operation: "clear",
              cause: state.error,
            }),
          );
        }
      },
      write: (writeSnapshot) => {
        const operation = writeSnapshot.value;
        const write =
          operation.kind === "save"
            ? persist("save", () => options.save(operation.observation))
            : persist("clear", () => options.clear(options.lessonId));

        return write.then(() => {
          const commit = {
            semanticRevision: writeSnapshot.expectedRevision + 1,
          };
          semanticRevision = commit.semanticRevision;
          if (operation.kind === "clear") {
            settleClearSuccess();
          }
          return commit;
        });
      },
    });
  }

  const makeSnapshot = (value: LessonProgressCommand) => ({
    entityId: options.lessonId,
    expectedRevision: semanticRevision,
    provenance: {
      kind: "autosave" as const,
      requestId: `lesson-progress-${++requestSequence}`,
      sessionId: `lesson-progress-${options.lessonId}`,
    },
    value,
  });

  coordinator = makeCoordinator();

  return {
    observe: (observation) => {
      if (!(acceptsObservations && observation.lessonId === options.lessonId)) {
        return;
      }
      aggregate = joinLessonProgressObservation(aggregate, observation);
      hasObservation = true;
    },
    flush: () => {
      if (!(acceptsObservations && hasObservation)) {
        return;
      }
      coordinator.submit(makeSnapshot({ kind: "save", observation: aggregate }));
    },
    clear: () => {
      const existingBarrier = clearBarrier;
      if (existingBarrier) {
        return existingBarrier.promise;
      }

      acceptsObservations = false;
      let resolve!: () => void;
      let reject!: (error: LessonProgressPersistenceError) => void;
      const barrier = {
        promise: new Promise<void>((resolvePromise, rejectPromise) => {
          resolve = resolvePromise;
          reject = rejectPromise;
        }),
        reject,
        resolve,
      } satisfies ClearBarrier;
      // The coordinator can reject before a browser caller attaches its UI
      // continuation; keep the rejection observable to callers without an
      // unhandled-rejection side channel.
      void barrier.promise.catch(() => null);
      clearBarrier = barrier;

      const stateBeforeClear = coordinator.state();
      const submission = coordinator.submit(makeSnapshot({ kind: "clear" }));
      if (!submission.accepted) {
        settleClearFailure(
          new LessonProgressPersistenceError({
            operation: "clear",
            cause: submission.error,
          }),
        );
        return barrier.promise;
      }
      if (stateBeforeClear.status === "blocked") {
        const rebase = coordinator.rebase(semanticRevision);
        if (!rebase.accepted) {
          settleClearFailure(
            new LessonProgressPersistenceError({
              operation: "clear",
              cause: rebase.error,
            }),
          );
        }
      }

      return barrier.promise;
    },
    state: () => coordinator.state(),
  };
}

export function useVideoProgress({
  lessonId,
  initialCompleted = false,
  completionThreshold = 80,
  saveInterval = 5000,
}: UseVideoProgressOptions) {
  const saveProgress = useSaveLessonProgress();
  const clearProgressMutation = useClearLessonProgress();
  const [progress, setProgress] = useState<VideoProgressData | null>(null);
  const [isCompleted, setIsCompleted] = useState(initialCompleted);
  const session = useMemo(
    () => ({
      axis: lessonId,
      cleared: false,
      completed: false,
      lastSaveTime: 0,
      latestProgress: null as VideoProgressData | null,
      lifetime: createComponentLifetime(),
      watchedSegments: new Set<number>(),
    }),
    [lessonId],
  );
  const lifetime = session.lifetime;
  if (initialCompleted && !session.cleared) {
    session.completed = true;
  }
  const writer = useMemo(
    () =>
      lessonId
        ? createLessonProgressWriter({
            lessonId,
            save: (observation) => saveProgress.executePromise(observation),
            clear: (stableLessonId) =>
              clearProgressMutation.executePromise({ lessonId: stableLessonId }),
          })
        : null,
    [lessonId, saveProgress.executePromise, clearProgressMutation.executePromise],
  );

  const flush = useCallback(() => {
    if (writer) {
      writer.flush();
    }
  }, [writer]);

  const recordObservation = useCallback(
    (currentProgress: VideoProgressData, forceComplete = false) => {
      if (!(lessonId && writer)) {
        return;
      }
      const currentSecond = Math.max(0, Math.floor(currentProgress.currentTime));
      session.watchedSegments.add(currentSecond);
      session.latestProgress = currentProgress;
      session.completed =
        session.completed ||
        forceComplete ||
        currentProgress.percentComplete >= completionThreshold;
      writer.observe({
        lessonId,
        watchedSeconds: session.watchedSegments.size,
        lastPosition: currentSecond,
        progressPercentage: Math.floor(currentProgress.percentComplete),
        completed: session.completed,
      });
      if (session.completed) {
        setIsCompleted(true);
      }
    },
    [completionThreshold, lessonId, session, writer],
  );

  const normalizeProgress = useCallback(
    ({ currentTime, duration }: { currentTime: number; duration: number }) => {
      if (!(Number.isFinite(currentTime) && Number.isFinite(duration)) || duration <= 0) {
        return null;
      }
      const normalizedTime = Math.max(0, currentTime);
      return {
        currentTime: normalizedTime,
        duration,
        percentComplete: Math.min(100, (normalizedTime / duration) * 100),
      } satisfies VideoProgressData;
    },
    [],
  );

  useEffect(() => {
    setProgress(null);
    setIsCompleted(session.completed);
    return () => {
      flush();
      lifetime.invalidate();
    };
  }, [flush, lifetime, session]);

  useEffect(() => {
    if (session.cleared) {
      if (!initialCompleted) {
        session.cleared = false;
      }
      return;
    }
    if (initialCompleted) {
      session.completed = true;
      setIsCompleted(true);
    }
  }, [initialCompleted, session]);

  // 处理时间更新事件
  const handleTimeUpdate = useCallback(
    ({ currentTime, duration }: { currentTime: number; duration: number }) => {
      const nextProgress = normalizeProgress({ currentTime, duration });
      if (!nextProgress) {
        return;
      }
      const wasCompleted = session.completed;
      setProgress(nextProgress);
      recordObservation(nextProgress);
      const now = unsafeDateTimeInputToEpochMillis(unsafeCurrentUtcDate());
      if ((!wasCompleted && session.completed) || now - session.lastSaveTime >= saveInterval) {
        session.lastSaveTime = now;
        flush();
      }
    },
    [flush, normalizeProgress, recordObservation, saveInterval, session],
  );

  // 处理视频结束事件
  const handleEnded = useCallback(() => {
    const completeProgress: VideoProgressData = {
      ...(session.latestProgress ?? {
        currentTime: 0,
        duration: 0,
        percentComplete: 0,
      }),
      percentComplete: 100,
    };
    setProgress(completeProgress);
    recordObservation(completeProgress, true);
    flush();
  }, [flush, recordObservation, session]);

  // 处理暂停事件
  const handlePause = useCallback(
    ({ currentTime, duration }: { currentTime: number; duration: number }) => {
      const nextProgress = normalizeProgress({ currentTime, duration });
      if (!nextProgress) {
        return;
      }
      setProgress(nextProgress);
      recordObservation(nextProgress);
      flush();
    },
    [flush, normalizeProgress, recordObservation],
  );

  // 处理拖动进度条事件
  const handleSeeked = useCallback(
    ({ currentTime, duration }: { currentTime: number; duration: number }) => {
      const nextProgress = normalizeProgress({ currentTime, duration });
      if (!nextProgress) {
        return;
      }
      setProgress(nextProgress);
      recordObservation(nextProgress);
      flush();
    },
    [flush, normalizeProgress, recordObservation],
  );

  const markComplete = useCallback(() => {
    handleEnded();
    return Promise.resolve();
  }, [handleEnded]);

  const clearProgress = useCallback(() => {
    if (!writer) {
      return Promise.resolve();
    }
    const clearing = writer.clear();
    lifetime.bind(clearing, {
      onSuccess: () => {
        session.cleared = true;
        session.completed = false;
        session.lastSaveTime = 0;
        session.latestProgress = null;
        session.watchedSegments.clear();
        setProgress(null);
        setIsCompleted(false);
      },
      onFailure: () => {
        // The mutation hook owns user-visible error reporting.
      },
    });
    return clearing;
  }, [lifetime, session, writer]);

  // 页面卸载时保存进度
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        flush();
      }
    };

    // 使用 visibilitychange 替代 beforeunload,更可靠
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [flush]);

  return {
    progress,
    isCompleted,
    watchedSeconds: session.watchedSegments.size,
    handleTimeUpdate,
    handleEnded,
    handlePause,
    handleSeeked,
    markComplete,
    clearProgress,
  };
}
